import React, { useMemo } from 'react'
import { getGeoJsonCenter } from '../utils/boundary'
import {
  buildGoogleMapsCoordinatesUrl,
  buildMapboxStaticImageUrl,
} from '../utils/mapbox'

const AuthorityBoundaryMap = ({
  latitude,
  longitude,
  boundaryGeoJson = null,
  className = '',
}) => {
  const boundaryCenter = useMemo(
    () => getGeoJsonCenter(boundaryGeoJson),
    [boundaryGeoJson]
  )

  const mapLatitude = latitude ?? boundaryCenter?.latitude ?? null
  const mapLongitude = longitude ?? boundaryCenter?.longitude ?? null
  const hasBoundary = Boolean(boundaryGeoJson)

  const mapUrl = useMemo(
    () =>
      buildMapboxStaticImageUrl(mapLongitude, mapLatitude, {
        boundaryGeoJson,
      }),
    [mapLongitude, mapLatitude, boundaryGeoJson]
  )

  if (mapLatitude == null || mapLongitude == null) {
    return null
  }

  const mapsLink = buildGoogleMapsCoordinatesUrl(mapLatitude, mapLongitude)

  if (!mapUrl) {
    return (
      <figure className={`shrink-0 w-full max-w-[360px] ${className}`}>
        <a
          href={mapsLink}
          target="_blank"
          rel="noopener noreferrer"
          className="block rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 p-4 text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
        >
          View {mapLatitude.toFixed(4)}, {mapLongitude.toFixed(4)} on Google Maps
        </a>
        {!hasBoundary && (
          <p className="mt-2 rounded-lg border border-amber-500/30 bg-amber-950/80 px-3 py-2 text-center text-xs font-medium text-amber-100">
            City Limits layer not found
          </p>
        )}
      </figure>
    )
  }

  return (
    <figure className={`shrink-0 w-full max-w-[360px] ${className}`}>
      <div className="relative overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-900/50">
        <a
          href={mapsLink}
          target="_blank"
          rel="noopener noreferrer"
          className="block"
          title="Open location in Google Maps"
        >
          <img
            src={mapUrl}
            alt={
              hasBoundary
                ? `Map showing city location and limits near ${mapLatitude.toFixed(4)}, ${mapLongitude.toFixed(4)}`
                : `Map showing city location at ${mapLatitude.toFixed(4)}, ${mapLongitude.toFixed(4)}`
            }
            width={320}
            height={200}
            className="block h-auto w-full object-cover"
            loading="lazy"
          />
        </a>

        {!hasBoundary && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 border-t border-amber-500/30 bg-amber-950/80 px-3 py-2 text-center text-xs font-medium text-amber-100">
            City Limits layer not found
          </div>
        )}
      </div>

      <figcaption className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px] text-gray-500 dark:text-gray-400">
        <a
          href={mapsLink}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
        >
          Open in Google Maps
        </a>
        <span aria-hidden="true">·</span>
        <a
          href="https://www.mapbox.com/about/maps/"
          target="_blank"
          rel="noopener noreferrer"
          className="hover:text-gray-700 dark:hover:text-gray-300"
        >
          © Mapbox
        </a>
        <span aria-hidden="true">·</span>
        <a
          href="https://www.openstreetmap.org/copyright"
          target="_blank"
          rel="noopener noreferrer"
          className="hover:text-gray-700 dark:hover:text-gray-300"
        >
          © OpenStreetMap
        </a>
      </figcaption>
    </figure>
  )
}

export default AuthorityBoundaryMap
